import type {
  CampaignStatus,
  CampaignSummary,
} from "@/services/campaigns.service";
import type { CampaignRecord } from "@/hooks/use-campaigns";
import { getInitials } from "./strings";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const compactNumberFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});
const markets: Record<string, string> = {
  BC: "British Columbia",
  CA: "California",
  NY: "New York",
  ON: "Ontario",
};

const campaignStatusLabels: Record<CampaignStatus, string> = {
  DRAFT: "Draft",
  ACTIVE: "Active",
  PAUSED: "Paused",
  ARCHIVED: "Archived",
  SCHEDULED: "Scheduled",
  ENDED: "Ended",
  OUTOFBUDGET: "Out of budget",
};

export function formatCampaignCurrencyFromMilliDollars(
  milliDollars: number,
): string {
  return currencyFormatter.format(Math.abs(milliDollars) / 1000);
}

export function getCampaignStatusLabel(status: CampaignStatus): string {
  return campaignStatusLabels[status] || "Unknown status";
}

function getCampaignMarket(campaign: CampaignSummary): string {
  if (!campaign.stateCode) {
    return "Unknown market";
  }

  return markets[campaign.stateCode] || campaign.stateCode;
}

export function getMarket(stateCode?: string): string {
  if (!stateCode) {
    return "Unknown market";
  }

  return markets[stateCode] || stateCode;
}

function getCampaignPlacements(campaign: CampaignSummary): string[] {
  return campaign.placementNames.length > 0
    ? campaign.placementNames
    : ["Unknown placement"];
}

export function formatCampaignDateLabel(date: string | null): string | null {
  if (!date) {
    return null;
  }

  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getFlightLabel(campaign: CampaignSummary): string {
  const startLabel = formatCampaignDateLabel(campaign.startDate);
  const endLabel = formatCampaignDateLabel(campaign.endDate);

  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`;
  }

  if (startLabel) {
    return `Starts ${startLabel}`;
  }

  if (endLabel) {
    return `Ends ${endLabel}`;
  }

  return "Schedule not set";
}

function getFlightProgress(campaign: CampaignSummary): number | null {
  if (!campaign.startDate || !campaign.endDate) {
    return null;
  }

  const start = new Date(campaign.startDate).getTime();
  const end = new Date(campaign.endDate).getTime();

  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
    return null;
  }

  const now = Date.now();

  if (now <= start) {
    return 0;
  }

  if (now >= end) {
    return 100;
  }

  return Math.round(((now - start) / (end - start)) * 100);
}

export function toCampaignRecord(campaign: CampaignSummary): CampaignRecord {
  const placements = getCampaignPlacements(campaign);

  return {
    index: 0,
    id: campaign.id,
    name: campaign.name,
    initials: getInitials(campaign.name),
    active: campaign.active,
    market: getCampaignMarket(campaign),
    placements,
    placementCount: placements.length,
    budget: formatCampaignCurrencyFromMilliDollars(campaign.budgetMilliDollars),
    budgetMilliDollars: campaign.budgetMilliDollars,
    spend: formatCampaignCurrencyFromMilliDollars(campaign.spendMilliDollars),
    spendMilliDollars: campaign.spendMilliDollars,
    impressions: campaign.impressions,
    impressionsLabel: compactNumberFormatter.format(campaign.impressions),
    ctr: campaign.ctr,
    ctrLabel: `${campaign.ctr.toFixed(2)}%`,
    status: campaign.status,
    updatedAt: new Date(campaign.updatedAt).toLocaleString(),
    flightLabel: getFlightLabel(campaign),
    flightProgress: getFlightProgress(campaign),
  };
}
